/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Thumbnail -> variant sync (Strideline).
 *
 * Ported from the Focal 8.11 build. The theme syncs variant changes INTO the
 * media gallery; this adds the reverse direction: clicking a gallery thumbnail
 * (or a mobile dots-nav item) selects the variant that image belongs to, so the
 * option selectors follow the gallery.
 *
 * A media item is mapped to variants by, in order:
 *   1. being a variant's featured image;
 *   2. its alt text naming an option value (our variant products give every
 *      image of a variant the option value as alt, e.g. "Toronto FC" - this
 *      covers variants with several images), including the Focal theme's
 *      grouping convention "... #OptionName_Value".
 * Media that maps to no variant (lifestyle shots, products without
 * variant-associated media) is left alone.
 *
 * FOCAL 13 NOTES - what changed from the 8.11 version:
 *   - <product-variants> is now <variant-picker>, and it no longer exposes
 *     .product or a selectVariant(id) method. Product JSON now comes from the
 *     <product-media> element, which loads it via the theme's ProductLoader.
 *   - Option inputs carry option-value IDs, not the value text, so a variant
 *     cannot be selected by value directly. Instead we resolve each value to
 *     its input by label text / aria-label and drive the theme's own inputs,
 *     letting variant-picker do the rendering.
 *   - All target options are set first, then ONE change event is dispatched:
 *     the theme reads the changed input plus the checked state of the others,
 *     so a single event applies the whole combination in one render.
 *   - data-bind-value is gone; the option value label is now
 *     .product-form__option-value inside .product-form__option-selector.
 */
(function () {
  function optionInputs(formId, position) {
    return Array.from(document.querySelectorAll(
      'input[data-option-position="' + position + '"][form="' + formId + '"]'
    ));
  }

  function inputLabelText(input) {
    var aria = input.getAttribute('aria-label');
    if (aria) {
      return aria.trim().toLowerCase();
    }
    if (input.id) {
      var label = document.querySelector('label[for="' + input.id + '"]');
      if (label) {
        return label.textContent.trim().toLowerCase();
      }
    }
    return null;
  }

  // Set (but do not fire) the input for one option value. Returns the input if
  // it changed, null if it was already selected or could not be resolved.
  function setOptionValue(formId, position, value) {
    var wanted = String(value).trim().toLowerCase();
    var inputs = optionInputs(formId, position);

    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (input.type !== 'radio') {
        continue;
      }
      if (inputLabelText(input) === wanted) {
        if (input.checked) {
          return null;
        }
        input.checked = true;
        return input;
      }
    }

    // Dropdown / combo-box: the value lives on a button, with a hidden input
    // holding the current selection.
    var hidden = inputs.find(function (input) { return input.type === 'hidden'; });
    if (hidden) {
      var selector = document.querySelector('variant-picker[form-id="' + formId + '"]');
      var button = selector && Array.from(
        selector.querySelectorAll('.combo-box__option-item[value]')
      ).find(function (item) {
        return item.textContent.trim().toLowerCase() === wanted;
      });
      if (button && button.value && button.value !== hidden.value) {
        hidden.value = button.value;
        return hidden;
      }
    }

    return null;
  }

  function pulseOptionLabel(formId, position) {
    var input = optionInputs(formId, position)[0];
    if (!input) {
      return;
    }
    var selector = input.closest('.product-form__option-selector');
    var valueElement = selector && selector.querySelector('.product-form__option-value');
    if (!valueElement) {
      return;
    }
    valueElement.classList.remove('variant-value-attention');
    void valueElement.offsetWidth; // restart the animation if one is mid-flight
    valueElement.classList.add('variant-value-attention');
    valueElement.addEventListener('animationend', function () {
      valueElement.classList.remove('variant-value-attention');
    }, { once: true });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest(
      '.product__thumbnail-item[data-media-id], .dots-nav__item[data-media-id]'
    );
    if (!button) {
      return;
    }

    var mediaElement = button.closest('product-media');
    if (!mediaElement) {
      return;
    }

    var product = mediaElement.product;
    if (!product || !product.variants) {
      return; // product data not loaded yet
    }

    var formId = mediaElement.getAttribute('form-id');
    var picker = document.querySelector('variant-picker[form-id="' + formId + '"]');
    if (!picker) {
      return; // no variant selector on this page
    }

    var mediaId = parseInt(button.getAttribute('data-media-id'), 10);
    var candidates = product.variants.filter(function (variant) {
      return variant.featured_media && variant.featured_media.id === mediaId;
    });

    if (candidates.length === 0) {
      // Not a featured image: map it through its alt text to an option value.
      var media = (product.media || []).find(function (m) { return m.id === mediaId; });
      if (media && media.alt) {
        var optionName = null;
        var value = media.alt;
        if (value.indexOf('#') !== -1) {
          var groupParts = value.split('#').pop().split('_');
          optionName = (groupParts[0] || '').trim().toLowerCase();
          value = groupParts[1] || '';
        }
        value = value.trim().toLowerCase();
        if (value) {
          (product.options || []).forEach(function (option, index) {
            if (candidates.length) {
              return;
            }
            var name = option.name || option;
            if (optionName && String(name).toLowerCase() !== optionName) {
              return;
            }
            var position = (option.position || index + 1) - 1;
            candidates = product.variants.filter(function (variant) {
              return (variant.options[position] || '').trim().toLowerCase() === value;
            });
          });
        }
      }
    }

    if (candidates.length === 0) {
      return;
    }

    // Several variants can share the image (e.g. all sizes of one team): keep as
    // much of the current selection as possible, then prefer an available one.
    var current = picker.selectedVariant;
    function sharedOptionCount(variant) {
      if (!current || !current.options) {
        return 0;
      }
      return variant.options.reduce(function (count, value, index) {
        return count + (value === current.options[index] ? 1 : 0);
      }, 0);
    }
    candidates.sort(function (a, b) {
      return (sharedOptionCount(b) - sharedOptionCount(a)) || (b.available - a.available);
    });

    var target = candidates[0];
    if (current && target.id === current.id) {
      return;
    }

    var previousOptions = (current && current.options) || [];
    var lastChanged = null;
    var changedPositions = [];

    target.options.forEach(function (value, index) {
      if (previousOptions[index] === value) {
        return;
      }
      var input = setOptionValue(formId, index + 1, value);
      if (input) {
        lastChanged = input;
        changedPositions.push(index + 1);
      }
    });

    if (!lastChanged) {
      return;
    }

    // One event applies the whole combination: the theme reads the changed
    // input plus the current checked state of every other option.
    lastChanged.dispatchEvent(new Event('change', { bubbles: true }));

    // Draw the eye to the option name(s) that just changed.
    changedPositions.forEach(function (position) {
      pulseOptionLabel(formId, position);
    });
  });
})();